import { Check, ChevronDown } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CadrageReadWithTeam } from '../../types';
import cadrageService from '../../services/cadrageService';

interface CadrageSelectorProps {
  useCaseId: string | null;
  selectedCadrageId: string | null;
  onCadrageChange: (cadrage: CadrageReadWithTeam | null) => void;
  className?: string;
  disabled?: boolean;
}

export const CadrageSelector: React.FC<CadrageSelectorProps> = ({
  useCaseId,
  selectedCadrageId,
  onCadrageChange,
  className = '',
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [cadrages, setCadrages] = useState<CadrageReadWithTeam[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadCadrages = async () => {
    if (!useCaseId) return;
    
    setIsLoading(true);
    try {
      const allCadrages = await cadrageService.getCadrages();
      // Filter by use_case_id
      const filtered = allCadrages.filter(c => c.use_case_id === useCaseId);
      setCadrages(filtered);
    } catch (error) {
      console.error('Error loading cadrages:', error);
      setCadrages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (useCaseId) {
      loadCadrages();
    } else {
      setCadrages([]);
      onCadrageChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCaseId]);

  const selectedCadrage = cadrages.find(c => c.id === selectedCadrageId);

  const handleSelect = (cadrage: CadrageReadWithTeam) => {
    onCadrageChange(cadrage);
    setIsOpen(false);
  };

  if (!useCaseId) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        {t('selectors.selectUseCaseFirst')}
      </div>
    );
  }

  return (
    <div className={`relative inline-block text-left w-full ${className}`}>
      <div>
        <button
          type="button"
          className="inline-flex w-full justify-between items-center rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <span className="truncate">{t('selectors.loading')}</span>
          ) : selectedCadrage ? (
            <span className="truncate" title={selectedCadrage.name}>{selectedCadrage.name}</span>
          ) : (
            <span className="text-muted-foreground truncate">{t('selectors.selectCadrage')}</span>
          )}
          <ChevronDown className="ml-2 h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </button>
      </div>

      {isOpen && !disabled && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 mt-2 w-full rounded-md border border-border bg-popover shadow-lg">
            <div className="py-1 max-h-60 overflow-y-auto">
              {cadrages.length === 0 ? (
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  {t('selectors.noCadrageInUseCase')}
                </div>
              ) : (
                cadrages.map((cadrage) => (
                  <button
                    key={cadrage.id}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground flex items-center justify-between"
                    onClick={() => handleSelect(cadrage)}
                  >
                    <span className="truncate" title={cadrage.name}>{cadrage.name}</span>
                    {cadrage.id === selectedCadrageId && (
                      <Check className="h-5 w-5 flex-shrink-0 text-primary ml-2" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
